import { useHookstate } from "@hookstate/core";
import React, { useEffect, useRef, useState } from "react";
import { simpleGetter } from "../../actions/common";
import { clubsApi } from "../../api/api";
import ArrowLeftWithBody from "../../assets/icons/ArrowLeftWithBody";
import CheckMark from "../../assets/icons/CheckMark";
import { useUniversalFind } from "../../hooks/universal";
import { clubFormStatusOptions } from "../../misc/constants";
import { getClubMeta, showSnackbar } from "../../misc/utils";
import { accessGlobalMiscState, useGlobalMiscState } from "../../stores/misc";
import {
  ClubCategory,
  ClubFormFields,
  MainUserClub,
  MainUserJsonSavedData,
  clubFormFields,
} from "../../types";
import { ImageUploadWithPreview } from "../ImageUploadWithPreview";
import { Switch } from "../Switch";
import { UserSelect } from "../UserSelect";
import InputField from "../input-field/InputField";
import SearchableSelect from "../input-field/SearchableSelect";

type ClubFormState = {
  form: ClubFormFields;
};

type ClubFormProps = {
  setVisible: (value: boolean) => void;
  clubType: any;
};

const ClubForm = ({ setVisible, clubType }: ClubFormProps) => {
  const miscState = useGlobalMiscState();
  const clubFormMode = miscState.get("clubFormMode") || "create";
  const clubFormEditItem = miscState.get("clubFormEditItem") || {};
  const clubCategories: ClubCategory[] = useUniversalFind("ClubCategory", {
    club_type_id: clubType?.id,
  });
  const [description, setDescription] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const localState = useHookstate({
    form: new ClubFormFields(),
  } as ClubFormState);
  const state = localState.get();
  const form = state.form;
  const clubTypeMeta = getClubMeta(clubType?.name);

  const updateForm = (key: keyof ClubFormFields, value: any) => {
    localState.form[key].set(value);
  };

  const onSelectMainUser = (user: MainUserJsonSavedData) => {
    updateForm("admin_main_user_id", user?.id);
    updateForm("admin_main_user_data", user);
    updateForm("admin_main_user_data_stringified", JSON.stringify(user));
  };

  const validateFormFields = () => {
    return (
      form.name &&
      form.admin_main_user_id &&
      form.description &&
      form.club_category_id
    );
  };

  const WordCount = ({ text }: { text: string }) => {
    const countWords = (text: string) =>
      text?.trim().split(/\s+/).filter(Boolean).length;
    const wordCount = countWords(text);
    return (
      <p
        className={`text-xs mx-5 ${
          wordCount > 300 ? "text-red-500" : "text-gray-500"
        }`}
      >
        {wordCount > 300
          ? "Максимальное количество слов превышено!"
          : `${wordCount}/300`}
      </p>
    );
  };

  useEffect(() => {
    if (clubFormMode === "edit" && clubFormEditItem.id) {
      const existingObject: Record<string, any> = {
        id: clubFormEditItem.id,
      };
      for (const f of clubFormFields) {
        if (f in clubFormEditItem) {
          existingObject[f] = clubFormEditItem[f];
        }
      }
      const existingAdmin: MainUserClub = clubFormEditItem.members?.find(
        (m: MainUserClub) =>
          m.club_members.status === 2 && m.club_members.is_admin === 1,
      );
      if (existingAdmin) {
        onSelectMainUser({ ...existingAdmin.json_saved_data });
      }
      console.log(existingObject);
      localState.form.merge(existingObject);
    }
  }, [clubFormMode, clubFormEditItem.id]);

  const onFormInputChangeRaw =
    (key: keyof ClubFormFields) => (value: any) => () => {
      updateForm(key, value);
    };

  const onFormInputChange =
    (key: keyof ClubFormFields, type: string) =>
    (
      e:
        | React.ChangeEvent<HTMLInputElement>
        | React.ChangeEvent<HTMLTextAreaElement>
        | React.ChangeEvent<HTMLSelectElement>,
    ) => {
      const target = e.target;

      switch (type) {
        case "file":
          if (target instanceof HTMLInputElement && target.files?.length) {
            const reader = new FileReader();
            reader.onload = function (event) {
              // @ts-ignore
              localState.form[`${key}Base64`].set(event.target?.result);
              // @ts-ignore
              localState.form[key].set(target.files?.[0]);
            };
            reader.readAsDataURL(target.files[0]);
          }
          break;
        case "text":
        case "select":
          updateForm(key, e.target.value);
          break;
        case "checkbox":
          if (target instanceof HTMLInputElement) {
            updateForm(key, target.checked ? 1 : 0);
          }
      }
    };

  const onSubmit = async () => {
    const formData = new FormData();
    for (const f of clubFormFields) {
      let value = form[f];

      if (typeof value === "number") {
        value = String(value);
      }

      if (value instanceof File || typeof value === "string") {
        formData.append(f, value);
      } else {
        console.warn(`Unsupported type for field "${f}":`, value);
      }
    }
    if (form.admin_main_user_data_stringified.length) {
      formData.append(
        "admin_main_user_data",
        form.admin_main_user_data_stringified,
      );
    }
    const response = await simpleGetter(clubsApi.createClub, formData);
    showSnackbar(
      response.success ? "Успешно" : "Произошла ошибка",
      response.success ? "success" : "error",
    );
    if (response.success) {
      accessGlobalMiscState().set(
        "allClubsTriggerFetch",
        (accessGlobalMiscState().get("allClubsTriggerFetch") || 0) + 1,
      );
      setVisible(false);
    }
  };

  const isButtonDisabled = !validateFormFields();
  const isEdit = clubFormMode === "edit";

  return (
    <div className='flex flex-col gap-6 p-2'>
      <div className='flex flex-col gap-1'>
        <p className='text-2xl font-semibold text-valley-black leading-tight'>
          {isEdit ? "Редактирование" : "Создание"} {clubTypeMeta.selfOfWhat}
        </p>
        <p className='text-sm text-valley-gray'>
          Заполните информацию {isEdit ? "для обновления" : "о новом"}{" "}
          {clubTypeMeta.selfOfWhat}
        </p>
      </div>

      <div className='flex flex-col md:flex-row gap-6'>
        <div className='md:basis-[40%] flex flex-col'>
          <ImageUploadWithPreview
            label={"Логотип организации"}
            valueBase64={form.logoBase64}
            value={form.logo}
            onChange={onFormInputChange("logo", "file")}
            placeholder='Загрузить логотип'
          />
        </div>

        <div className='md:basis-[60%] flex flex-col gap-5'>
          <InputField
            value={form.name}
            handleChange={onFormInputChange("name", "text")}
            type='text'
            label={`Название ${clubTypeMeta.selfOfWhat}`}
            id='clubName'
            placeholder={`Введите название ${clubTypeMeta.selfOfWhat}`}
            additionalClassName='px-4 py-3 !bg-valley-background-blue border-transparent rounded-lg !placeholder-valley-light-gray !opacity-100'
          />

          {clubType?.name === "club" && (
            <InputField
              checked={Boolean(form.is_main)}
              handleChange={onFormInputChange("is_main", "checkbox")}
              placeholder=''
              label='Главный клуб'
              id='isMainClub'
              type='checkbox'
            />
          )}

          <UserSelect
            label={`Президент ${clubTypeMeta.selfOfWhat}`}
            placeholder='Выберите президента'
            fieldId='club-admin-select'
            selectedUser={form.admin_main_user_data}
            handleChange={onSelectMainUser}
            source='internal'
          />
        </div>
      </div>

      <div className='flex flex-col gap-2'>
        <label
          htmlFor='description'
          className='block text-sm font-semibold text-valley-gray'
        >
          Краткое описание
        </label>
        <textarea
          id='description'
          value={form.description}
          onChange={onFormInputChange("description", "text")}
          className='border border-transparent rounded-lg px-4 py-3 w-full focus:outline-none focus:border-valley-main bg-valley-background-blue placeholder-valley-light-gray h-[120px] text-valley-black resize-none'
          placeholder='Текст описания'
        />
        <WordCount text={form.description} />
      </div>

      <div className='flex flex-col md:flex-row gap-5 md:items-end'>
        <div className='md:basis-1/2'>
          <SearchableSelect
            value={form.club_category_id ? String(form.club_category_id) : ""}
            handleChange={onFormInputChange("club_category_id", "select")}
            label='Направление'
            id='category'
            additionalClassName='!bg-valley-background-blue border border-transparent py-3 px-4'
            options={clubCategories.map((c) => ({
              value: c.id.toString(),
              label: c.name,
            }))}
          />
        </div>

        <Switch
          wrapperClassName='md:basis-1/2'
          id='club-status-switch'
          value={form.status}
          handleChange={onFormInputChangeRaw("status")}
          label='Видимость'
          options={clubFormStatusOptions}
        />
      </div>

      <div className='flex flex-col sm:flex-row gap-3 mt-2'>
        <button
          type='button'
          onClick={() => setVisible(false)}
          className='flex items-center gap-2 justify-center basis-1/2 px-4 py-3 bg-valley-gray rounded-lg text-white font-semibold hover:opacity-90 transition-opacity'
        >
          <ArrowLeftWithBody />
          Вернуться
        </button>
        <button
          type='button'
          className={`flex items-center gap-2 justify-center basis-1/2 px-4 py-3 bg-valley-main rounded-lg text-white font-semibold transition-opacity ${
            isButtonDisabled
              ? "opacity-40 cursor-not-allowed"
              : "hover:opacity-90"
          }`}
          disabled={isButtonDisabled}
          onClick={onSubmit}
        >
          <CheckMark />
          {isEdit ? "Обновить" : "Создать"} {clubTypeMeta.createButton}
        </button>
      </div>
    </div>
  );
};

export default ClubForm;
